import React, { useContext, useEffect, useState } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import RefreshDataContext, { RefreshChannelOptions } from 'shared/components/context/refresh-data'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { IChannel } from 'shared/types/channel'
import { CollectionContext } from '..'
import ChannelListItem, {
    AddChannelListItem,
    EditChannelListItem,
    ExpandToDrop,
    LoaderListItem,
    ViewChannelListItem,
} from './channel-list-item'
import { AddToCollectionContext } from './context'

const ChannelList = ({ channel }: { channel: IChannel }) => {
    const { isReordering } = useContext(AddToCollectionContext)
    const [isLoading, setLoading] = useState(true)
    const [children, setChildren] = useState<IChannel[]>(channel.children || [])
    const { onRefreshChannel } = useContext(RefreshDataContext)
    const { getChannelChildren, channels, rootChannelId } = useContext(CollectionContext)
    const fetchChildren = async channel => {
        const channelListChildren = await getChannelChildren(channel)
        setChildren(channelListChildren)
    }

    useAsyncEffect(async () => {
        if (channel.id !== 0) {
            setLoading(true)
            try {
                await fetchChildren(channel)
            } catch (error) {
                console.error(`Error fetching collection cabinet channel ${channel.id}`)
            }
        }
        setLoading(false)
    }, [channel.id])
    useEffect(() => {
        const off = onRefreshChannel((updatedChannel, options: RefreshChannelOptions) => {
            if (channel.id === updatedChannel.id) {
                if (updatedChannel.has_children) {
                    if (options.isReordering) {
                        setChildren(updatedChannel.children)
                    } else {
                        fetchChildren(updatedChannel)
                    }
                } else {
                    setChildren([])
                }
            }
        })
        return off
    }, [channel.id])
    // TODO this is never true anymore, since we don't show root anymore
    const isRootOfEverything = channel.id === 0 //  0 is the root
    const isNotRootOfCabinet = rootChannelId !== channel.id // a user account isn't a collection it's a channel, is what we want to convey
    if (isLoading) {
        return <LoaderListItem />
    }
    return (
        <>
            {channel.has_children ? (
                <Droppable droppableId={`${channel.id}`}>
                    {({ innerRef }) => (
                        <div ref={innerRef}>
                            {children.map((channel, index) =>
                                channels[channel.id] ? (
                                    <Draggable
                                        key={channel.id}
                                        draggableId={channel.id.toString()}
                                        index={index}
                                        isDragDisabled={isReordering}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.dragHandleProps}
                                                {...provided.draggableProps}
                                            >
                                                <ChannelListItem
                                                    isDragging={snapshot.isDragging}
                                                    key={channel.id}
                                                    channel={channels[channel.id]}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                ) : null
                            )}
                        </div>
                    )}
                </Droppable>
            ) : (
                <></>
            )}
            {!isRootOfEverything && (
                <Droppable droppableId={`${channel.id}-empty`}>
                    {({ innerRef }, { isDraggingOver }) => (
                        <div ref={innerRef}>
                            {!channel.has_children && isDraggingOver && (
                                <ExpandToDrop>Move collection here...</ExpandToDrop>
                            )}
                            <AddChannelListItem channel={channel} isDisabled={isDraggingOver} />
                            {isNotRootOfCabinet && (
                                <>
                                    <EditChannelListItem channel={channel} isDisabled={isDraggingOver} />
                                    <ViewChannelListItem channel={channel} isDisabled={isDraggingOver} />
                                </>
                            )}
                        </div>
                    )}
                </Droppable>
            )}
        </>
    )
}

// ChannelList.whyDidYouRender = true

export default ChannelList
